'use client';

import { memo } from 'react';
import { Typography, Icon } from '../../kit';
import { themeColors } from '../../theme/theme';
import { Box, LinearProgress } from '@mui/material';
import { commonStyles } from '../../styles/shared/commonStyles';
import { useTranslation } from '@/kit/useTranslation';

const CARD_STEP_KEYS = [
  { key: 'origin', labelKey: 'common.from', icon: 'home' },
  { key: 'destination', labelKey: 'common.to', icon: 'LocationOn' },
  { key: 'size', labelKey: 'common.size', icon: 'Inventory2' },
  { key: 'type', labelKey: 'common.type', icon: 'Category' },
];

const StepCard = memo(({ step, completed, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      background: completed ? 'linear-gradient(135deg, #e3f0ff 0%, #f6fafd 100%)' : '#f6fafd',
      p: { xs: 1.5, sm: 2 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      minWidth: { xs: 70, sm: 80 },
      minHeight: { xs: 80, sm: 90 },
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        boxShadow: 2,
      },
    }}
  >
    <Icon
      name={step.icon || 'help_outline'}
      style={{
        fontSize: 22,
        color: completed ? themeColors.primaryColor : '#bbb',
        marginBottom: 8,
      }}
    />
    <Typography
      type="body2"
      text={step.label}
      style={{
        fontWeight: 400,
        color: completed ? themeColors.primaryColor : '#bbb',
        fontSize: { xs: 12, sm: 14 },
      }}
    />
    {completed && (
      <Icon
        name="check"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: themeColors.primaryColor,
          fontSize: 18,
        }}
      />
    )}
  </Box>
));
StepCard.displayName = 'StepCard';

const EstimatorProgressBar = memo(({ origin, destination, size, types, onCardClick }) => {
  const { t } = useTranslation();
  const steps = [
    { ...CARD_STEP_KEYS[0], label: t(CARD_STEP_KEYS[0].labelKey), value: origin },
    { ...CARD_STEP_KEYS[1], label: t(CARD_STEP_KEYS[1].labelKey), value: destination },
    { ...CARD_STEP_KEYS[2], label: t(CARD_STEP_KEYS[2].labelKey), value: size },
    {
      ...CARD_STEP_KEYS[3],
      label: t(CARD_STEP_KEYS[3].labelKey),
      value: types && types.length ? types.join(', ') : '',
    },
  ];

  const completed = steps.map((s) => Boolean(s.value));
  const progress = (completed.filter(Boolean).length / steps.length) * 100;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
        mt: 3,
        px: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          border: `2px solid ${themeColors.primaryColor}`,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'white',
          mb: 1.5,
        }}
      >
        {steps.map((step, idx) => (
          <Box key={step.key} sx={{ display: 'flex', flex: 1 }}>
            <StepCard
              step={step}
              completed={completed[idx]}
              onClick={() => onCardClick && onCardClick(step.key)}
            />
            {idx < steps.length - 1 && (
              <Box
                sx={{
                  width: 2,
                  bgcolor: themeColors.primaryColor,
                  alignSelf: 'stretch',
                }}
              />
            )}
          </Box>
        ))}
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 1,
          bgcolor: '#e3f0ff',
          '& .MuiLinearProgress-bar': {
            bgcolor: themeColors.primaryColor,
            borderRadius: 1,
          },
        }}
      />
    </Box>
  );
});

EstimatorProgressBar.displayName = 'EstimatorProgressBar';

export default EstimatorProgressBar;
